import { useEffect, useMemo, useState } from "react";
import {
  FiCalendar,
  FiCheckCircle,
  FiDollarSign,
  FiDroplet,
  FiScissors,
  FiUsers,
  FiXCircle,
} from "react-icons/fi";
import StatsCard from "../components/admin/StatsCard";
import AdminCard from "../components/admin/AdminCard";
import AdminTable from "../components/admin/AdminTable";
import Loader from "../components/common/Loader";
import { BOOKING_STATUS } from "../utils/constants";
import { formatDate, formatTime } from "../utils/formatDate";
import formatCurrency from "../utils/formatCurrency";
import * as serviceService from "../services/serviceService";
import * as bookingService from "../services/bookingService";
import * as userService from "../services/userService";

const safeCallGetAllServices = async () => {
  if (typeof serviceService.getAllServices === "function") {
    return serviceService.getAllServices();
  }

  if (typeof serviceService.getServices === "function") {
    return serviceService.getServices();
  }

  throw new Error("Service list method was not found in serviceService.");
};

const safeCallGetAllBookings = async () => {
  if (typeof bookingService.getAllBookings === "function") {
    return bookingService.getAllBookings();
  }

  if (typeof bookingService.getBookings === "function") {
    return bookingService.getBookings();
  }

  if (typeof bookingService.getAllAdminBookings === "function") {
    return bookingService.getAllAdminBookings();
  }

  throw new Error("Booking list method was not found in bookingService.");
};

const safeCallGetAllUsers = async () => {
  if (typeof userService.getAllUsers === "function") {
    return userService.getAllUsers();
  }

  if (typeof userService.getUsers === "function") {
    return userService.getUsers();
  }

  throw new Error("User list method was not found in userService.");
};

const getBookingHennaType = (booking) => {
  return booking?.hennaType || booking?.service?.hennaType || "—";
};

const Dashboard = () => {
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setLoading(true);
        setError("");

        const [servicesResponse, bookingsResponse, usersResponse] = await Promise.all([
          safeCallGetAllServices(),
          safeCallGetAllBookings(),
          safeCallGetAllUsers(),
        ]);

        const servicesData = servicesResponse?.data || servicesResponse;
        const bookingsData = bookingsResponse?.data || bookingsResponse;
        const usersData = usersResponse?.data || usersResponse;

        if (servicesData?.statusCode && servicesData.statusCode !== 200) {
          throw new Error(servicesData.message || "Failed to load services.");
        }

        if (bookingsData?.statusCode && bookingsData.statusCode !== 200) {
          throw new Error(bookingsData.message || "Failed to load bookings.");
        }

        if (usersData?.statusCode && usersData.statusCode !== 200) {
          throw new Error(usersData.message || "Failed to load users.");
        }

        setServices(Array.isArray(servicesData?.serviceList) ? servicesData.serviceList : []);
        setBookings(Array.isArray(bookingsData?.bookingList) ? bookingsData.bookingList : []);
        setUsers(Array.isArray(usersData?.userList) ? usersData.userList : []);
      } catch (err) {
        setError(err?.message || "Unable to load admin dashboard.");
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  const stats = useMemo(() => {
    const confirmed = bookings.filter(
      (booking) => booking.status === BOOKING_STATUS.CONFIRMED
    ).length;

    const canceled = bookings.filter(
      (booking) => booking.status === BOOKING_STATUS.CANCELED
    ).length;

    const completed = bookings.filter(
      (booking) => booking.status === BOOKING_STATUS.COMPLETED
    ).length;

    const activeServices = services.filter(
      (service) => service.active !== false
    ).length;

    const revenueTotal = bookings.reduce((sum, booking) => {
      if (
        booking?.status !== BOOKING_STATUS.CONFIRMED &&
        booking?.status !== BOOKING_STATUS.COMPLETED
      ) {
        return sum;
      }

      return sum + (Number(booking?.bookingPrice) || 0);
    }, 0);

    const hennaCounts = bookings.reduce((acc, booking) => {
      const hennaType = getBookingHennaType(booking);
      acc[hennaType] = (acc[hennaType] || 0) + 1;
      return acc;
    }, {});

    const topHennaType =
      Object.entries(hennaCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";

    return {
      totalServices: services.length,
      activeServices,
      totalBookings: bookings.length,
      confirmed,
      canceled,
      completed,
      totalUsers: users.length,
      revenueTotal,
      topHennaType,
    };
  }, [services, bookings, users]);

  const recentBookings = useMemo(() => {
    return [...bookings]
      .sort((a, b) => {
        const dateA = new Date(`${a.bookingDate || ""}T${a.startTime || "00:00"}`);
        const dateB = new Date(`${b.bookingDate || ""}T${b.startTime || "00:00"}`);
        return dateB - dateA;
      })
      .slice(0, 6);
  }, [bookings]);

  if (loading) {
    return <Loader text="Loading dashboard..." />;
  }

  return (
    <div className="space-y-6">
      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-700">{error}</p>
        </div>
      ) : null}

      <div className="responsive-three-col">
        <StatsCard
          title="Total Services"
          value={stats.totalServices}
          subtitle={`${stats.activeServices} currently active`}
          icon={<FiScissors size={22} />}
        />

        <StatsCard
          title="Total Bookings"
          value={stats.totalBookings}
          subtitle={`${stats.confirmed} confirmed bookings`}
          icon={<FiCalendar size={22} />}
        />

        <StatsCard
          title="Registered Users"
          value={stats.totalUsers}
          subtitle="Client records in the system"
          icon={<FiUsers size={22} />}
        />
      </div>

      <div className="responsive-three-col">
        <StatsCard
          title="Confirmed"
          value={stats.confirmed}
          subtitle="Currently confirmed appointments"
          icon={<FiCheckCircle size={22} />}
        />

        <StatsCard
          title="Canceled"
          value={stats.canceled}
          subtitle="Bookings canceled by admin or client"
          icon={<FiXCircle size={22} />}
        />

        <StatsCard
          title="Completed"
          value={stats.completed}
          subtitle="Services already completed"
          icon={<FiCheckCircle size={22} />}
        />
      </div>

      <div className="responsive-three-col">
        <StatsCard
          title="Revenue Total"
          value={formatCurrency(stats.revenueTotal)}
          subtitle="Confirmed and completed booking totals"
          icon={<FiDollarSign size={22} />}
        />

        <StatsCard
          title="Top Henna Type"
          value={stats.topHennaType}
          subtitle="Most selected across all bookings"
          icon={<FiDroplet size={22} />}
        />

        <StatsCard
          title="Active Services"
          value={stats.activeServices}
          subtitle="Services currently bookable"
          icon={<FiScissors size={22} />}
        />
      </div>

      <AdminCard
        title="Recent Booking Activity"
        subtitle="A quick view of the most recent booking entries across the platform."
      >
        <div className="max-h-[420px] overflow-y-auto pr-1">
          <AdminTable
            data={recentBookings}
            emptyMessage="No bookings have been made yet."
            columns={[
              {
                key: "service",
                label: "Service",
                render: (row) => row?.service?.serviceName || "—",
              },
              {
                key: "hennaType",
                label: "Henna Type",
                render: (row) => getBookingHennaType(row),
              },
              {
                key: "client",
                label: "Client / Guest",
                render: (row) => {
                  if (row?.user) {
                    return `${row.user.firstname || ""} ${row.user.lastname || ""}`.trim() || row.user.email || "User";
                  }

                  if (row?.guest) {
                    return `${row.guest.firstName || ""} ${row.guest.lastName || ""}`.trim() || row.guest.email || "Guest";
                  }

                  return "—";
                },
              },
              {
                key: "date",
                label: "Date",
                render: (row) => (row?.bookingDate ? formatDate(row.bookingDate) : "—"),
              },
              {
                key: "time",
                label: "Time",
                render: (row) =>
                  row?.startTime && row?.endTime
                    ? `${formatTime(row.startTime)} - ${formatTime(row.endTime)}`
                    : "—",
              },
              {
                key: "price",
                label: "Price",
                render: (row) =>
                  row?.bookingPrice !== undefined && row?.bookingPrice !== null
                    ? formatCurrency(row.bookingPrice)
                    : "—",
              },
              {
                key: "status",
                label: "Status",
                render: (row) => {
                  const status = row?.status || "—";
                  const statusClass =
                    status === BOOKING_STATUS.CONFIRMED
                      ? "status-chip status-chip-confirmed"
                      : status === BOOKING_STATUS.CANCELED
                      ? "status-chip status-chip-canceled"
                      : status === BOOKING_STATUS.COMPLETED
                      ? "status-chip status-chip-completed"
                      : "status-chip";

                  return <span className={statusClass}>{status}</span>;
                },
              },
            ]}
          />
        </div>
      </AdminCard>
    </div>
  );
};

export default Dashboard;